/**
 * Очередь подтверждения гостевого доступа (owner/manager).
 *
 * Два ОТДЕЛЬНЫХ экрана (правило feedback_every_form_is_a_separate_screen):
 *   1. Список pending (GET /v1/guest-access/pending) — карточки заявок.
 *   2. Провал в заявку → ОТДЕЛЬНЫЙ экран коррекции: правка дат/номера +
 *      «Подтвердить» (POST .../approve) / «Отклонить» (POST .../reject).
 *      «Назад» возвращает к списку. Рендерится ТОЛЬКО форма (список не виден).
 *
 * RBAC — guest_access.approve (owner/manager), гейтится на бэке.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { GuestAccessGrant } from '@hsc/types';
import { useRooms } from '../rooms/useRooms.js';
import {
  useApproveGuestAccess,
  usePendingGuestAccess,
  useRejectGuestAccess,
} from './useGuestAccess.js';

function ruDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

function ruDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function PendingApprovalsPage(): JSX.Element {
  const navigate = useNavigate();
  const pendingQ = usePendingGuestAccess();
  const approve = useApproveGuestAccess();
  const reject = useRejectGuestAccess();
  const [selected, setSelected] = useState<GuestAccessGrant | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Быстрое «Принять» (без коррекции дат) / «Отклонить» прямо из карточки —
  // без проваливания в форму. Изменить даты → клик «Изменить» откроет форму.
  function quickApprove(id: string): void {
    setBusyId(id);
    approve.mutate(
      { id, input: {} },
      { onSettled: () => setBusyId(null), onSuccess: () => void pendingQ.refetch() },
    );
  }
  function quickReject(id: string): void {
    setBusyId(id);
    reject.mutate(id, {
      onSettled: () => setBusyId(null),
      onSuccess: () => void pendingQ.refetch(),
    });
  }

  const grants = useMemo(() => pendingQ.data?.grants ?? [], [pendingQ.data]);

  // ОТДЕЛЬНЫЙ экран коррекции — рендерим только его.
  if (selected) {
    return (
      <ApprovalForm
        grant={selected}
        onDone={() => {
          setSelected(null);
          void pendingQ.refetch();
        }}
        onBack={() => setSelected(null)}
      />
    );
  }

  return (
    <section data-testid="pending-approvals-page" className="ga-page">
      <header className="staff-topbar">
        <div>
          <button
            type="button"
            className="ta-back-btn"
            onClick={() => navigate('/guest-access')}
            data-testid="pending-back"
          >
            <span className="ta-back-btn__chev" aria-hidden="true">
              ‹
            </span>{' '}
            Назад
          </button>
          <h1 className="hsc-page__title staff-topbar__title">
            Очередь подтверждения{grants.length > 0 ? ` · ${grants.length}` : ''}
          </h1>
        </div>
      </header>

      {pendingQ.isLoading ? (
        <div className="fleet-list" data-testid="pending-skeleton">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={72} radius={14} />
          ))}
        </div>
      ) : pendingQ.isError ? (
        <Card>
          <p role="alert">Не удалось загрузить очередь. Обновите страницу.</p>
        </Card>
      ) : grants.length === 0 ? (
        <Card>
          <p data-testid="pending-empty" className="stays-empty">
            Заявок на подтверждение нет. Когда гость попробует войти без активного доступа, его
            заявка появится здесь.
          </p>
        </Card>
      ) : (
        <ul className="ga-pending-list" data-testid="pending-list">
          {grants.map((g) => (
            <li key={g.id} className="ga-pending-card" data-testid={`pending-card-${g.id}`}>
              <div className="ga-pending-card__top">
                <strong className="ga-pending-card__room">
                  {g.surname || 'Без фамилии'} · № {g.room_number || '—'}
                </strong>
                <span className="fleet-badge fleet-badge--busy">Ожидает</span>
              </div>
              <div className="ga-pending-card__dates">
                Заезд {ruDate(g.check_in)} → выезд {ruDate(g.check_out)}
              </div>
              <div className="ga-pending-card__meta">Заявка от {ruDateTime(g.created_at)}</div>
              <div className="ga-pending-card__actions">
                <Button
                  size="sm"
                  loading={busyId === g.id && approve.isPending}
                  disabled={busyId !== null}
                  onClick={() => quickApprove(g.id)}
                  data-testid={`pending-approve-${g.id}`}
                >
                  Принять
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={busyId === g.id && reject.isPending}
                  disabled={busyId !== null}
                  onClick={() => quickReject(g.id)}
                  data-testid={`pending-reject-${g.id}`}
                >
                  Отклонить
                </Button>
                <button
                  type="button"
                  className="staff-link"
                  onClick={() => setSelected(g)}
                  data-testid={`pending-edit-${g.id}`}
                >
                  Изменить даты
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ---------- ОТДЕЛЬНЫЙ экран: форма коррекции + подтвердить/отклонить ---------- */

function ApprovalForm({
  grant,
  onDone,
  onBack,
}: {
  grant: GuestAccessGrant;
  onDone: () => void;
  onBack: () => void;
}): JSX.Element {
  const roomsQ = useRooms({ limit: 500 });
  const approve = useApproveGuestAccess();
  const reject = useRejectGuestAccess();

  const [checkIn, setCheckIn] = useState(grant.check_in.slice(0, 10));
  const [checkOut, setCheckOut] = useState(grant.check_out.slice(0, 10));
  const [roomId, setRoomId] = useState(grant.room_id ?? '');

  const rooms = useMemo(() => roomsQ.data?.items ?? [], [roomsQ.data]);
  const datesValid = checkIn !== '' && checkOut !== '' && checkOut > checkIn;
  const busy = approve.isPending || reject.isPending;

  const doApprove = (): void => {
    if (!datesValid) return;
    const input: { check_in?: string; check_out?: string; room_id?: string } = {
      check_in: checkIn,
      check_out: checkOut,
    };
    if (roomId !== '') input.room_id = roomId;
    approve.mutate({ id: grant.id, input }, { onSuccess: onDone });
  };

  const doReject = (): void => {
    reject.mutate(grant.id, { onSuccess: onDone });
  };

  return (
    <section data-testid="approval-form-page" className="ga-page">
      <header className="staff-topbar">
        <div>
          <div className="staff-topbar__crumbs">
            <button type="button" className="staff-link" onClick={onBack}>
              Очередь подтверждения
            </button>{' '}
            / Заявка
          </div>
          <h1 className="hsc-page__title staff-topbar__title">Подтверждение доступа</h1>
        </div>
      </header>

      <form
        className="ta-card ga-form"
        onSubmit={(e) => {
          e.preventDefault();
          doApprove();
        }}
        data-testid="approval-form"
      >
        <p className="ga-result-lead">
          Заявка по номеру <strong>№ {grant.room_number || '—'}</strong>. Проверьте и при
          необходимости скорректируйте номер и даты — затем подтвердите.
        </p>

        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="ap-room">
            Номер комнаты
          </label>
          {rooms.length > 0 ? (
            <select
              id="ap-room"
              className="ta-select"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              data-testid="approval-room-select"
            >
              <option value="">— по номеру «{grant.room_number || '—'}» —</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  № {r.number}
                </option>
              ))}
            </select>
          ) : (
            <input id="ap-room" className="ta-input" value={`№ ${grant.room_number}`} readOnly />
          )}
        </div>

        <div className="ta-field">
          <span className="hsc-input__label">Даты проживания</span>
          <div className="ga-dates">
            <label className="ga-dates__col">
              <span className="ga-dates__cap">Заезд</span>
              <input
                type="date"
                className="ta-input"
                value={checkIn}
                onChange={(e) => setCheckIn(e.target.value)}
                data-testid="approval-checkin"
                required
              />
            </label>
            <label className="ga-dates__col">
              <span className="ga-dates__cap">Выезд</span>
              <input
                type="date"
                className="ta-input"
                value={checkOut}
                min={checkIn}
                onChange={(e) => setCheckOut(e.target.value)}
                data-testid="approval-checkout"
                required
              />
            </label>
          </div>
          {!datesValid && (
            <p className="staff-hint ga-hint--warn">Дата выезда должна быть позже заезда.</p>
          )}
        </div>

        {(approve.isError || reject.isError) && (
          <p role="alert" className="staff-invite__msg staff-invite__msg--err" data-testid="approval-error">
            Не удалось выполнить действие. Попробуйте ещё раз.
          </p>
        )}

        <div className="staff-modal__actions ga-actions">
          <button
            type="button"
            className="staff-btn staff-btn--ghost"
            onClick={onBack}
            data-testid="approval-back"
          >
            Назад
          </button>
          <button
            type="button"
            className="staff-btn staff-btn--danger"
            onClick={doReject}
            disabled={busy}
            data-testid="approval-reject"
          >
            {reject.isPending ? 'Отклоняем…' : 'Отклонить'}
          </button>
          <button
            type="submit"
            className="staff-btn"
            disabled={busy || !datesValid}
            data-testid="approval-approve"
          >
            {approve.isPending ? 'Подтверждаем…' : 'Подтвердить'}
          </button>
        </div>
      </form>
    </section>
  );
}
